import { isEmpty } from 'lodash';
import { observer } from 'mobx-react';
import { productsArrayPropTypes } from '@nandosaus/prop-types';
import PropTypes from 'prop-types';
import { useRootStore } from '@nandosaus/state-management';

import { HeadingWithFlameHighlights } from '@components/heading-with-flame-highlights';
import { useBrazeImpression } from '@hooks/use-braze-impression';
import { DaypartWarning } from '@components/daypart-warning';
import { CardListing } from '../../card-listing';
import { P } from '../../typography/new';
import { useFeature } from '../../../hooks/use-feature';

const ProductCategory = ({ category, viewProduct, CardComponent, quantityPicker, hideDescription }) => {
  const showCategoryDescription = useFeature('menu-category-descriptions');
  const hasDescription = !isEmpty(category.description);
  const { brazeLogImpression } = category;
  const { OrderingContextStore, MenuStore } = useRootStore();
  const { restaurant } = OrderingContextStore;

  useBrazeImpression({ brazeLogImpression });

  // Keep component mounted but hide with CSS to allow interval to keep running
  return (
    <>
      <HeadingWithFlameHighlights heading={category.name} isLabel variant="category" />
      {showCategoryDescription && hasDescription && (
        <P variant="medium" mt="16px" width={{ xl: 9 / 12 }}>
          {category.description}
        </P>
      )}
      {!MenuStore.isCateringMenu && (
        <DaypartWarning
          mt="16px"
          daypart={category.daypart}
          timezone={restaurant?.timezone}
          categoryName={category.name}
        />
      )}
      <CardListing
        mt={{ xs: '28px', lg: '40px' }}
        CardComponent={CardComponent}
        quantityPicker={quantityPicker}
        hideDescription={hideDescription}
        gridTemplateColumns={{
          xs: 'repeat(2, minmax(0, 1fr))',
          sm: 'repeat(3, minmax(0, 1fr))',
          lg: 'repeat(4, minmax(0, 1fr))',
        }}
        gap={{ xs: '40px 15px', md: '60px 24px' }}
        categoryName={category.name}
        items={category.products}
        onClick={item => {
          viewProduct(item);
          category?.brazeLogClick?.();
        }}
        cardBackgroundColor="transparent"
      />
    </>
  );
};

ProductCategory.propTypes = {
  CardComponent: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
  category: PropTypes.shape({
    name: PropTypes.string.isRequired,
    handle: PropTypes.string.isRequired,
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    daypart: PropTypes.object,
    products: productsArrayPropTypes,
    brazeLogImpression: PropTypes.func,
    brazeLogClick: PropTypes.func,
  }).isRequired,
  viewProduct: PropTypes.func.isRequired,
  quantityPicker: PropTypes.bool,
  hideDescription: PropTypes.bool,
};

ProductCategory.defaultProps = {
  quantityPicker: false,
  hideDescription: false,
};

const ComposedProductCategory = observer(ProductCategory);
export { ComposedProductCategory as ProductCategory };
